import type { AppTheme } from '@backstage/core-plugin-api';
import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
  UnifiedThemeProvider,
} from '@backstage/theme';
import type { Theme, ThemeOptions } from '@mui/material/styles';
import type { ReactNode } from 'react';
import LightMode from '@mui/icons-material/LightMode';
import DarkMode from '@mui/icons-material/DarkMode';

type PageThemeMap = Record<string, ReturnType<typeof genPageTheme>>;
type ProviderProps = { children?: ReactNode };

const borderRadiusLarge = 12;
const borderRadiusSmall = 8;

const brand = {
  primary: '#0050FF',
  primarySoft: '#8CBCFF',
  navy: '#081736',
  navyMuted: '#13294B',
  accent: '#00E0B8',
  accentSoft: '#7B61FF',
  warning: '#F79009',
  success: '#12B76A',
  error: '#F04438',
  info: '#1A6DFF',
};

const typography = {
  htmlFontSize: 16,
  fontFamily:
    '"InterVariable", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
  h1: {
    fontSize: 48,
    fontWeight: 700,
    marginBottom: 16,
  },
  h2: {
    fontSize: 36,
    fontWeight: 700,
    marginBottom: 12,
  },
  h3: {
    fontSize: 30,
    fontWeight: 600,
    marginBottom: 10,
  },
  h4: {
    fontSize: 26,
    fontWeight: 600,
    marginBottom: 8,
  },
  h5: {
    fontSize: 20,
    fontWeight: 600,
    marginBottom: 6,
  },
  h6: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 4,
  },
};

const componentOverrides: ThemeOptions['components'] = {
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        borderRadius: 999,
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: '0.01em',
        paddingInline: '1.25rem',
        paddingBlock: '0.65rem',
      },
    },
  },
  BackstageInfoCard: {
    styleOverrides: {
      headerTitle: {
        fontWeight: 400,
        fontSize: 'var(--bui-font-size-6)',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: borderRadiusLarge,
      },
      elevation1: ({ theme }: { theme: Theme }) => ({
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0px 18px 40px rgba(4, 8, 20, 0.55)'
            : '0px 18px 36px rgba(8, 23, 54, 0.12)',
      }),
      elevation2: ({ theme }: { theme: Theme }) => ({
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0px 24px 45px rgba(4, 8, 20, 0.6)'
            : '0px 24px 40px rgba(8, 23, 54, 0.14)',
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 999,
        fontWeight: 500,
        letterSpacing: '0.02em',
      },
    },
  },
  BackstageSelectInputBase: {
    styleOverrides: {
      input: {
        borderRadius: borderRadiusSmall,
        '&:focus': {
          borderRadius: borderRadiusSmall,
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        borderRadius: 999,
        height: 4,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: '0.02em',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: 'none',
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      colorPrimary: {
        backdropFilter: 'blur(12px)',
        backgroundColor: 'rgba(8, 23, 54, 0.72)',
      },
      colorDefault: {
        backdropFilter: 'blur(12px)',
        backgroundColor: 'rgba(15, 27, 54, 0.8)',
      },
    },
  },
  CatalogReactUserListPicker: {
    styleOverrides: {
      title: {
        display: 'block',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: borderRadiusSmall,
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      rounded: {
        overflow: 'hidden',
        '&:first-child': {
          borderTopLeftRadius: borderRadiusLarge,
          borderTopRightRadius: borderRadiusLarge,
        },
        '&:last-child': {
          borderBottomLeftRadius: borderRadiusLarge,
          borderBottomRightRadius: borderRadiusLarge,
        },
      },
    },
  },
};

const schibstedPageThemes: PageThemeMap = {
  home: genPageTheme({
    colors: [brand.primary, brand.accent],
    shape: shapes.wave,
  }),
  documentation: genPageTheme({
    colors: ['#1E1B4B', brand.accentSoft],
    shape: shapes.wave2,
  }),
  tool: genPageTheme({
    colors: [brand.primary, brand.accentSoft],
    shape: shapes.round,
  }),
  service: genPageTheme({
    colors: [brand.primary, '#1A2A6C'],
    shape: shapes.wave,
  }),
  website: genPageTheme({
    colors: ['#172554', brand.primary],
    shape: shapes.wave2,
  }),
  library: genPageTheme({
    colors: ['#312E81', '#7C3AED'],
    shape: shapes.wave,
  }),
  other: genPageTheme({
    colors: ['#0F172A', brand.navyMuted],
    shape: shapes.wave,
  }),
  app: genPageTheme({
    colors: [brand.primary, '#FF7A45'],
    shape: shapes.wave2,
  }),
  apis: genPageTheme({
    colors: [brand.accent, '#2DD4BF'],
    shape: shapes.round,
  }),
  card: genPageTheme({
    colors: [brand.primary, brand.accent],
    shape: shapes.wave,
  }),
};

const lightPalette = {
  ...palettes.light,
  background: {
    default: '#EFF4FC',
    paper: '#FFFFFF',
  },
  primary: {
    main: brand.primary,
  },
  secondary: {
    main: brand.accent,
  },
  status: {
    ...palettes.light.status,
    ok: brand.success,
    warning: brand.warning,
    error: brand.error,
    running: brand.primary,
    pending: '#FDB022',
    aborted: '#475467',
  },
  banner: {
    ...palettes.light.banner,
    info: brand.primary,
    warning: brand.warning,
    error: brand.error,
    link: '#021633',
    text: '#FFFFFF',
    closeButtonColor: '#FFFFFF',
  },
  border: '#D4E1F4',
  textContrast: '#021633',
  textVerySubtle: '#8CA2C2',
  textSubtle: '#4A607F',
  highlight: '#EDF3FF',
  errorBackground: '#FFF0EF',
  warningBackground: '#FFF8EB',
  infoBackground: '#E6F0FF',
  errorText: '#D92D20',
  infoText: '#1F4ED8',
  warningText: '#B54708',
  linkHover: '#0039B8',
  link: brand.primary,
  navigation: {
    background: brand.navy,
    indicator: brand.accent,
    color: '#94A3B8',
    selectedColor: '#FFFFFF',
    navItem: {
      hoverBackground: brand.navyMuted,
    },
    submenu: {
      background: brand.navyMuted,
    },
  },
  pinSidebarButton: {
    icon: '#FFFFFF',
    background: '#1B2F59',
  },
  tabbar: {
    indicator: brand.accent,
  },
};

const darkPalette = {
  ...palettes.dark,
  background: {
    default: '#0F1C31',
    paper: '#142338',
  },
  primary: {
    main: brand.primarySoft,
  },
  secondary: {
    main: brand.accent,
  },
  status: {
    ...palettes.dark.status,
    ok: brand.success,
    warning: brand.warning,
    error: brand.error,
    running: brand.primarySoft,
    pending: '#F8D06A',
    aborted: '#4B5565',
  },
  banner: {
    ...palettes.dark.banner,
    info: brand.primarySoft,
    warning: brand.warning,
    error: brand.error,
    link: '#E2E8F0',
    text: '#FFFFFF',
    closeButtonColor: '#FFFFFF',
  },
  border: '#FFFFFF26',
  textContrast: '#F5F8FF',
  textVerySubtle: '#3D5A82',
  textSubtle: '#87A0C2',
  highlight: '#233A5A',
  errorBackground: '#3B1219',
  warningBackground: '#302008',
  infoBackground: '#0F1B36',
  errorText: '#F97066',
  infoText: '#79B6FF',
  warningText: '#FEC84B',
  linkHover: '#7EB5F7',
  link: '#9CC9FF',
  navigation: {
    background: '#040814',
    indicator: brand.accent,
    color: '#94A3B8',
    selectedColor: '#FFFFFF',
    navItem: {
      hoverBackground: '#102043',
    },
    submenu: {
      background: '#102043',
    },
  },
  pinSidebarButton: {
    icon: '#101821',
    background: '#3A4D75',
  },
  tabbar: {
    indicator: brand.accent,
  },
  code: {
    background: '#0B1220',
  },
};

const schibstedLightTheme = createUnifiedTheme({
  palette: lightPalette,
  defaultPageTheme: 'home',
  pageTheme: schibstedPageThemes,
  fontFamily: typography.fontFamily,
  typography,
  components: componentOverrides,
});

const schibstedDarkTheme = createUnifiedTheme({
  palette: darkPalette,
  defaultPageTheme: 'home',
  pageTheme: schibstedPageThemes,
  fontFamily: typography.fontFamily,
  typography,
  components: componentOverrides,
});

const SchibstedLightProvider = ({ children }: ProviderProps) => (
  <UnifiedThemeProvider theme={schibstedLightTheme}>
    {children}
  </UnifiedThemeProvider>
);

const SchibstedDarkProvider = ({ children }: ProviderProps) => (
  <UnifiedThemeProvider theme={schibstedDarkTheme}>
    {children}
  </UnifiedThemeProvider>
);

export const schibstedThemes: AppTheme[] = [
  {
    id: 'schibsted-light',
    title: 'Schibsted Light',
    variant: 'light',
    icon: <LightMode />,
    Provider: SchibstedLightProvider,
  },
  {
    id: 'schibsted-dark',
    title: 'Schibsted Dark',
    variant: 'dark',
    icon: <DarkMode />,
    Provider: SchibstedDarkProvider,
  },
];

export { schibstedLightTheme, schibstedDarkTheme };
